import React, { useState, useEffect } from 'react';
import { X, Sparkles, ChevronRight, CheckCircle2, Waves, HeartHandshake } from 'lucide-react';

interface TaglinePhilosophyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TaglinePhilosophyModal: React.FC<TaglinePhilosophyModalProps> = ({ isOpen, onClose }) => {
  const [activeStep, setActiveStep] = useState<number>(1);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const storySteps = [
    {
      step: 1,
      tag: 'Bahagian I: Asal-Usul',
      title: 'Ilham Alam & Srikandi Semerah',
      subtitle: 'Ciptaan Gerak Kacak Hati',
      content: `Dalam dunia persilatan Melayu, alam sentiasa menjadi guru yang paling setia. Tumbuh-tumbuhan, haiwan, dan aliran sungai mencorakkan gerak tempur dan keluhuran budi pendekar.\n\nIlmu persilatan ini bermula daripada inspirasi seorang srikandi bernama Semerah yang mencipta gerak 'Kacak Hati' berpandukan pergerakan alam semula jadi. Ilmu rahsia ini kemudiannya diperturunkan kepada adik angkatnya, Awang Sulung.`,
      highlight: 'Seni Tempur berinspirasikan alam semula jadi & keluhuran adab.'
    },
    {
      step: 2,
      tag: 'Bahagian II: Ujian Pendekar',
      title: 'Ujian Sang Pendekar Muda',
      subtitle: 'Pertarungan Usia 18 Tahun',
      content: `Kemuncak penceritaan berlaku ketika Awang Sulung menginjak ke usia 18 tahun. Beliau yang bergelar Pendekar Muda pada waktu itu telah dicabar hebat oleh seorang Panglima Raja yang angkuh dan sombong lagaknya.\n\nBerlakulah sebuah pertarungan yang sengit. Namun, berbekalkan kebijaksanaan seni tempur dan ketangkasan gerak tubuhnya, Awang Sulung berjaya menumbangkan panglima yang gah tersebut.`,
      highlight: 'Kemenangan menerusi kebijaksanaan seni tempur & ketenangan fikiran.'
    },
    {
      step: 3,
      tag: 'Bahagian III: Kemaafan & Penyatuan',
      title: 'Biduk Berlalu, Kiambang Bertaut',
      subtitle: 'Kemenangan Tanpa Kesombongan',
      content: `Bagi pemuda belasan tahun yang mengalahkan panglima besar, ego kebiasaannya akan melonjak tinggi. Namun, Awang Sulung menundukkan egonya dan memilih jalan damai.\n\nTindakan luhur Awang memaafkan musuhnya digambarkan ibarat pepatah "Biduk berlalu, kiambang bertaut". Sengketa hanyalah 'perahu' yang melintas seketika, dan selepas semuanya tamat, ukhuwah kembali bercantum umpama 'kiambang' di permukaan air.\n\nDaripada detik bersejarah itulah, seni bela diri ini dirasmikan dengan nama Awang Sulung Merah Muda; menggabungkan kemuliaan hati Awang dan kehebatan kakaknya, Semerah.`,
      highlight: 'Penggabungan nama Awang Sulung (kemuliaan hati) & Semerah (Merah Muda).'
    },
    {
      step: 4,
      tag: 'Bahagian IV: Jantung Falsafah',
      title: 'Mesej Buat Pesilat Hari Ini',
      subtitle: 'Intipati Pegangan ASMM',
      content: `"Sehebat mana pun kita di tengah gelanggang, setajam mana pun buah pukulan kita menumbangkan lawan, nilai sebenar seorang pendekar terletak pada kelembutan hatinya memaafkan.\n\nSilat itu mencari kawan, bukan menempah lawan. Bila tamat persengketaan, buanglah ego jauh-jauh dan bertautlah kembali sebagai saudara."`,
      highlight: 'Silat untuk membina persaudaraan & kedamaian batiniah.'
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div
        className="relative w-full max-w-2xl bg-white border border-slate-200 rounded-3xl shadow-2xl overflow-hidden my-6 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-slate-900 text-white p-5 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="font-extrabold text-base text-white tracking-tight">
                Falsafah "Biduk Berlalu, Kiambang Bertaut"
              </h3>
              <p className="text-xs text-slate-400">Jantung Falsafah Persatuan Seni Silat ASMM</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
            aria-label="Tutup"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
          {/* Step Progress Navigator */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200">
            {storySteps.map((s) => {
              const isActive = activeStep === s.step;
              return (
                <button
                  key={s.step}
                  onClick={() => setActiveStep(s.step)}
                  className={`py-2 px-2.5 rounded-xl text-xs font-extrabold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${isActive
                    ? 'bg-white text-asmm-red shadow-sm border border-slate-200/80'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                >
                  <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold ${isActive ? 'bg-asmm-red text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                    {s.step}
                  </span>
                  <span className="truncate">{s.title.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Active Story Card */}
          {storySteps.map((s) => {
            if (s.step !== activeStep) return null;
            return (
              <div key={s.step} className="space-y-4 animate-fade-in">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-asmm-red bg-red-50 px-2 py-0.5 rounded border border-red-100">
                      {s.tag}
                    </span>
                    <h4 className="text-base font-extrabold text-slate-900 mt-1">
                      {s.title}
                    </h4>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <button
                      disabled={activeStep === 1}
                      onClick={() => setActiveStep(prev => Math.max(1, prev - 1))}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 disabled:opacity-40 hover:bg-slate-50"
                    >
                      Sebelum
                    </button>
                    <button
                      disabled={activeStep === 4}
                      onClick={() => setActiveStep(prev => Math.min(4, prev + 1))}
                      className="px-2.5 py-1 rounded-lg bg-asmm-red text-white text-xs font-bold shadow-xs disabled:opacity-40 hover:bg-asmm-crimson flex items-center gap-1"
                    >
                      <span>Seterusnya</span>
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                  {s.step === 4 ? (
                    <blockquote className="text-xs sm:text-sm italic font-semibold text-slate-800 leading-relaxed border-l-4 border-asmm-red pl-3.5 py-0.5 whitespace-pre-line">
                      {s.content}
                    </blockquote>
                  ) : (
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line font-medium">
                      {s.content}
                    </p>
                  )}

                  <div className="pt-2.5 border-t border-slate-200/80 flex items-center gap-2 text-xs font-bold text-slate-900">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>{s.highlight}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};
